import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpHeaders,
  HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { WalletStateService } from 'src/app/shared/services/wallet-state.service';
import { CsrfTokenService } from 'src/app/shared/services/csrf-token.service';
import { communityHeaders } from 'src/app/core/services/co-brand.service';

// QUERY is a read like GET, and the backend's CSRF check treats it as safe too.
const CSRF_SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS', 'QUERY'];

@Injectable()
export class RegulatedInterceptor implements HttpInterceptor {
  constructor(
    private readonly walletState: WalletStateService,
    private readonly csrfTokenService: CsrfTokenService
  ) { }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.isBackendRequest(request.url)) {
      return next.handle(request);
    }

    const regulated = String(this.walletState.getRegulated());
    const chainId = String(this.walletState.getNetwork()?.networkId ?? this.walletState.getWallet()?.networkId ?? environment.DEFAULT_NETWORK);

    let headers: HttpHeaders = request.headers
      .set('regulated', regulated)
      .set('Chain-id', chainId);

    // Community-Id only while the page shows a community (?community_id= in the URL)
    Object.entries(communityHeaders()).forEach(([name, value]) => {
      headers = headers.set(name, value);
    });

    const csrfToken = this.csrfTokenService.get();
    if (csrfToken && !CSRF_SAFE_METHODS.includes(request.method.toUpperCase())) {
      headers = headers.set('X-CSRF-Token', csrfToken);
    }

    const modifiedReq = request.clone({
      headers,
      withCredentials: true
    });

    // Capture the session CSRF token from any backend response that carries
    // one (login, googleAuth, wallet verify, /auth/me boot probe, ...).
    return next.handle(modifiedReq).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const token = (event.body as { data?: { csrf_token?: string } })?.data?.csrf_token;
          if (token) {
            this.csrfTokenService.set(token);
          }
        }
      })
    );
  }

  private isBackendRequest(url: string): boolean {
    return [
      environment.API_BASE_URL,
      environment.API_BASE_URL_V2,
      environment.API_BASE_URL_V3,
      environment.FILE_UPLOAD
    ].some((baseUrl) => url.startsWith(baseUrl));
  }
}
